import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';

export function useFormErrorHandler() {
  const showErrorNotification = useErrorNotifications();

  return useCallback(
    (error): never => {
      showErrorNotification(error);
      throw getErrorBody(error);
    },
    [showErrorNotification]
  );
}

export function useErrorNotifications() {
  const { enqueueSnackbar } = useSnackbar();

  return useCallback(
    (error) => {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    },
    [enqueueSnackbar]
  );
}

export function getErrorBody(
  error: AxiosError
): Record<string, string> | string {
  return getErrors(error) || getErrorMessage(error);
}

export function getErrorMessage(error: AxiosError): string {
  if (error && error.response) {
    const message = error.response.data && error.response.data.message;
    const errors = error.response.data && error.response.data.errors;
    return `Palvelinvirhe ${error.response.status}: ${message ||
      'Tuntematon'} ${errors ? JSON.stringify(errors, null, 2) : ''}`;
  }

  return 'Yhteysvirhe.';
}

export function getErrors<T>(
  error: AxiosError
): Record<keyof T, string> | undefined {
  const errors = error.response!.data!.errors as Record<keyof T, string[]>;

  if (errors) {
    const errorsWithMessage = Object.keys(errors).map((field) => {
      // Combine all the field messages into one.
      return [field, errors[field].join(' ')];
    });

    // Transform the errors into field->error object
    return errorsWithMessage.reduce(
      (messages, [field, message]) => {
        messages[field] = message;
        return messages;
      },
      {} as any
    );
  }

  return undefined;
}
